import { useNotificationStore } from '../store/notificationStore';
import { Notification } from '../types/notifications';

export class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectInterval: number = 5000;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private isIntentionallyClosed: boolean = false;
  private url: string;

  constructor(url?: string) {
    // Use environment variable or default to local development
    this.url = url || import.meta.env.VITE_WS_URL || 'ws://localhost:8081/ws';
  }

  connect(token: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    this.isIntentionallyClosed = false;

    try {
      // Include auth token in connection URL or as a protocol
      this.ws = new WebSocket(`${this.url}?token=${token}`);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        useNotificationStore.getState().setConnectionStatus(true);
        this.startHeartbeat();

        // Clear any existing reconnect timer
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        useNotificationStore.getState().setConnectionStatus(false);
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        useNotificationStore.getState().setConnectionStatus(false);
        this.stopHeartbeat();

        // Attempt to reconnect if not intentionally closed
        if (!this.isIntentionallyClosed) {
          this.scheduleReconnect(token);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect(token);
    }
  }

  private handleMessage(data: any) {
    switch (data.type) {
      case 'notification':
        this.handleNotification(data.payload);
        break;

      case 'notification_batch':
        this.handleNotificationBatch(data.payload);
        break;

      case 'heartbeat':
        // Server heartbeat response - connection is alive
        break;

      case 'system':
        this.handleSystemMessage(data.payload);
        break;

      default:
        console.warn('Unknown WebSocket message type:', data.type);
    }
  }

  private handleNotification(notification: Partial<Notification>) {
    const store = useNotificationStore.getState();

    // Add the notification to the store
    store.addNotification({
      type: notification.type || 'info',
      title: notification.title || 'New Notification',
      message: notification.message || '',
      priority: notification.priority || 'medium',
      source: notification.source,
      actionUrl: notification.actionUrl,
      actionLabel: notification.actionLabel,
      metadata: notification.metadata,
      projectId: notification.projectId,
      contractId: notification.contractId,
    });
  }

  private handleNotificationBatch(notifications: Partial<Notification>[]) {
    notifications.forEach(notification => {
      this.handleNotification(notification);
    });
  }

  private handleSystemMessage(message: any) {
    const store = useNotificationStore.getState();

    // Handle system-wide messages (maintenance, updates, etc.)
    store.showToast({
      type: 'info',
      title: 'System Message',
      message: message.text,
      duration: 10000,
    });
  }

  private startHeartbeat() {
    // Send heartbeat every 30 seconds to keep connection alive
    this.heartbeatTimer = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, 30000);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(token: string) {
    if (this.reconnectTimer) {
      return; // Already scheduled
    }

    console.log(`Scheduling reconnect in ${this.reconnectInterval}ms`);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(token);
    }, this.reconnectInterval);
  }

  disconnect() {
    this.isIntentionallyClosed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHeartbeat();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    useNotificationStore.getState().setConnectionStatus(false);
  }

  send(data: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket is not connected');
    }
  }

  // Request notification history from server
  requestHistory(since?: Date) {
    this.send({
      type: 'request_history',
      payload: {
        since: since?.toISOString(),
      },
    });
  }

  // Mark notification as read on server
  markAsRead(notificationId: string) {
    this.send({
      type: 'mark_read',
      payload: {
        notificationId,
      },
    });
  }

  // Update notification preferences on server
  updatePreferences(preferences: any) {
    this.send({
      type: 'update_preferences',
      payload: preferences,
    });
  }
}

// Singleton instance
let wsService: WebSocketService | null = null;

export const getWebSocketService = (): WebSocketService => {
  if (!wsService) {
    wsService = new WebSocketService();
  }
  return wsService;
};

// Hook to use WebSocket in components
export const useWebSocket = () => {
  const service = getWebSocketService();

  const connect = (token: string) => {
    service.connect(token);
  };

  const disconnect = () => {
    service.disconnect();
  };

  const send = (data: any) => {
    service.send(data);
  };

  return {
    connect,
    disconnect,
    send,
    requestHistory: service.requestHistory.bind(service),
    markAsRead: service.markAsRead.bind(service),
    updatePreferences: service.updatePreferences.bind(service),
  };
};